import type React from 'react';
import { push } from 'connected-react-router';
import Link from '@zg-rentals/multibrand-ui/lib/Link';
import Notification from '@zg-rentals/multibrand-ui/lib/Notification';
import CheckCircleFill from '@zg-rentals/multibrand-ui/lib/icons/CheckCircleFill';
import ProblemTriangleFill from '@zg-rentals/multibrand-ui/lib/icons/ProblemTriangleFill';
import InfoCircle from '@assets/info.svg';
import { acceptTerms, saveRentalProtectionFeature, startNewTerm } from '@rental-manager/actions/OnboardingActions';
import { editingRentTerms } from '@rental-manager/actions/DashboardActions';
import { closeNotification } from '@common/actions/NotificationActions';
import {
  getIreneDashboard,
  saveRentalRelationshipIdToPolicy,
  sendNotification,
  toggleFeature,
} from '@rental-manager/actions/requests';
import notificationTypes from '@common/constants/notificationTypes';
import StringConstants from '@common/constants/StringConstants';
import paths from '@rental-manager/pathsManifest';
import '../../common/components/NotificationContainer/index.scss';
import type {
  ContinuePaymentSetupProps,
  CustomNotificationProps,
  InBetweenTermsProps,
  InviteNewRenterProps,
  MoveInCostPaymentLimitErrorProps,
  PaymentTermEligibleForRentalProtectionPolicyProps,
  RentalProtectionCoverageRequestNotificationProps,
} from './types';
import { checkRequestActionSuccess } from '@common/paymentsUtils/api';
import FeaturePermissionTypes from '@rental-manager/constants/FeaturePermissionTypes';

const HostedOnboardingRequired = ({
  onClose,
  dispatch,
  baseUrl,
  className,
}: CustomNotificationProps): React.ReactNode => (
  <Notification className={className} type={notificationTypes.DEFAULT} onClose={onClose}>
    You signed up for online payments. Our payment partner, Stripe, needs more info to verify your account.&nbsp;
    <Link
      className="PaymentsNotification-Link"
      onClick={() =>
        Promise.resolve()
          .then(() => dispatch(closeNotification()))
          .then(() => dispatch(push(baseUrl + paths.dashboard.stripeVerificationPage)))
      }
    >
      Add info
    </Link>
  </Notification>
);

const ContinuePaymentSetup = ({
  onClose,
  dispatch,
  baseUrl,
  propertyId,
  className,
}: CustomNotificationProps & ContinuePaymentSetupProps): React.ReactNode => (
  <Notification className={className} type={notificationTypes.DEFAULT} onClose={onClose}>
    You haven&apos;t finished setting up your payment.&nbsp;
    <Link
      className="PaymentsNotification-Link"
      onClick={() =>
        Promise.resolve()
          .then(() => dispatch(closeNotification()))
          .then(() => dispatch(acceptTerms({ propertyId })))
          .then(() =>
            dispatch(
              push({
                pathname: baseUrl + paths.onboarding.rentAmountPage,
                search: 'acceptTerms=true',
              }),
            ),
          )
      }
    >
      Continue payment setup
    </Link>
  </Notification>
);

const InviteNewRenter = ({
  onClose,
  dispatch,
  baseUrl,
  propertyId,
  className,
  rentTerms,
  rentTermsExtendPermissionCurrentTerm,
  leasesForProperty,
}: CustomNotificationProps & InviteNewRenterProps): React.ReactNode => (
  <Notification className={className} type={notificationTypes.DEFAULT} onClose={onClose}>
    Your rent payments have ended.&nbsp;
    <Link
      className="PaymentsNotification-Link"
      onClick={() =>
        Promise.resolve()
          .then(() => dispatch(closeNotification()))
          .then(() => dispatch(startNewTerm({ propertyId })))
          .then(() => {
            dispatch(
              push({
                pathname:
                  baseUrl +
                  (leasesForProperty.length > 0 ? paths.onboarding.leaseSelectorPage : paths.onboarding.rentAmountPage),
                search: 'acceptTerms=true',
              }),
            );
          })
      }
    >
      Invite a new tenant
    </Link>
    {rentTermsExtendPermissionCurrentTerm?.isAllowed && (
      <>
        &nbsp;or&nbsp;
        <Link
          className="PaymentsNotification-Link"
          onClick={() =>
            Promise.resolve()
              .then(() => dispatch(closeNotification()))
              .then(() => dispatch(editingRentTerms(rentTerms)))
              .then(() =>
                dispatch(
                  push({
                    pathname: baseUrl + paths.dashboard.editPaymentLengthPage,
                    search: `rentTermsKey=${rentTerms?.rentTermsKey}`,
                  }),
                ),
              )
          }
        >
          add more payments
        </Link>
        &nbsp;for your existing tenant.
      </>
    )}
  </Notification>
);

const MoveInCostPaymentLimitError = ({ onClose, className }: MoveInCostPaymentLimitErrorProps): React.ReactNode => (
  <Notification className={className} type={notificationTypes.ERROR} onClose={onClose}>
    The total of your move-in costs cannot be $10K or more. Please reduce the total or&nbsp;
    <Link className="PaymentsNotification-Link" href={StringConstants.LINKS.SUPPORT_PAGE.LANDLORD} target="_blank">
      contact us
    </Link>
    .
  </Notification>
);

const InBetweenTerms = ({
  onClose,
  className,
  previousTermEnded,
  nextTermStarts,
}: InBetweenTermsProps): React.ReactNode => (
  <Notification className={className} type={notificationTypes.INFO} onClose={onClose}>
    Your previous term ended on {previousTermEnded}. Your next term starts {nextTermStarts}.
  </Notification>
);

const RentalProtectionFirstRentPaymentFailed = ({ onClose, tenantEmail }: CustomNotificationProps): React.ReactNode => (
  <Notification type={notificationTypes.DEFAULT} onClose={onClose}>
    <ProblemTriangleFill colorType="inverse" size={18} className="mrm IconAlignment" />
    The tenant&apos;s first rent payment failed. Rental Protection insurance will be automatically canceled if the
    tenant doesn&apos;t pay rent within 60 days of the first day of the lease.&nbsp;
    <Link className="PaymentsNotification-Link" href={`mailto:${tenantEmail}`}>
      Contact tenant
    </Link>
  </Notification>
);

const RentalProtectionLatestRentPaymentFailed = ({
  onClose,
  tenantEmail,
}: CustomNotificationProps): React.ReactNode => (
  <Notification type={notificationTypes.DEFAULT} onClose={onClose}>
    <ProblemTriangleFill colorType="inverse" size={18} className="mrm IconAlignment" />
    The rent payment failed. If rent isn&apos;t paid within five days of its due date, the Rental Protection premium
    will be automatically debited from your account.&nbsp;
    <Link className="PaymentsNotification-Link" href={`mailto:${tenantEmail}`}>
      Contact tenant
    </Link>
  </Notification>
);

const RentalProtectionCoverageRequestNotSigned = ({
  onClose,
  className,
  listingAlias,
  policyId,
}: RentalProtectionCoverageRequestNotificationProps): React.ReactNode => (
  <Notification type={notificationTypes.DEFAULT} onClose={onClose} className={className}>
    <ProblemTriangleFill colorType="inverse" size={18} className="mrm IconAlignment" />
    Your request to purchase Rental Protection hasn&apos;t been sent. To request it, sign and send the request before
    payment is processed.&nbsp;
    <Link
      className="PaymentsNotification-Link"
      to={`${StringConstants.LINKS.RENTAL_PROTECTION.MANAGE_POLICY_PAGE(
        listingAlias,
        policyId,
      )}?signCoverageRequest=true`}
      target="_blank"
    >
      Sign Now
    </Link>
    .
  </Notification>
);

const RentalProtectionPolicyPending = ({ onClose }: CustomNotificationProps): React.ReactNode => (
  <Notification type={notificationTypes.SUCCESS} onClose={onClose}>
    <CheckCircleFill colorType="inverse" size={18} className="mrm IconAlignment" />
    You invited your tenant to set up online payments. We&apos;ll notify you when they&apos;re done.
  </Notification>
);

const RentalProtectionMissedRentPayment = ({
  onClose,
  dispatch,
  baseUrl,
}: CustomNotificationProps): React.ReactNode => (
  <Notification type={notificationTypes.DEFAULT} onClose={onClose}>
    <ProblemTriangleFill colorType="inverse" size={18} className="mrm IconAlignment" />
    &nbsp;
    <strong>Rent is past due.&nbsp;</strong>Your Rental Protection coverage may be affected.&nbsp;
    <Link
      className="PaymentsNotification-Link"
      onClick={() =>
        Promise.resolve()
          .then(() => dispatch(closeNotification()))
          .then(() => dispatch(push(baseUrl + paths.dashboard.handleMissedRent)))
      }
    >
      See next steps
    </Link>
  </Notification>
);

const CoverageRequestError = ({ onClose }: CustomNotificationProps): React.ReactNode => (
  <Notification type={notificationTypes.ERROR} onClose={onClose}>
    Coverage request can&apos;t be signed due to an error. Please refresh or try again again later.&nbsp;
    <Link className="PaymentsNotification-Link" href={`mailto:rentalprotection@zillowinsuranceservices.com`}>
      Contact Customer Support
    </Link>
    &nbsp;if the issue persists.
  </Notification>
);

const PaymentTermEligibleForRentalProtectionPolicy = ({
  dispatch,
  propertyId,
  paymentsEncodedLeaseId,
  rentTermsKey,
  onClose,
}: CustomNotificationProps & PaymentTermEligibleForRentalProtectionPolicyProps): React.ReactNode => (
  <Notification
    className="PaymentTermEligibleForRentalProtectionPolicy-banner"
    type={notificationTypes.INFO}
    onClose={onClose}
  >
    <img src={InfoCircle as unknown as string} width={24} height={24} alt="" className="InfoIcon" />
    &nbsp; Still interested in Rental Protection for this payment term?&nbsp;
    <Link
      className="PaymentsNotification-Link"
      onClick={() =>
        Promise.resolve()
          .then(() => dispatch(closeNotification()))
          .then(() =>
            dispatch(
              saveRentalRelationshipIdToPolicy({
                propertyId,
                paymentsEncodedLeaseId,
                rentTermsKey,
              }),
            ),
          )
          .then(checkRequestActionSuccess)
          .then(() =>
            dispatch(
              toggleFeature({
                propertyId,
                featureName: FeaturePermissionTypes.RENT_GUARANTEE,
                on: true,
              }),
            ),
          )
          .then(checkRequestActionSuccess)
          .then(() => dispatch(saveRentalProtectionFeature({ propertyId, rentGuarantee: true })))
          .then(() =>
            dispatch(
              sendNotification({
                propertyId,
                notificationType: 'ireneSignRequestForCoverage',
              }),
            ),
          )
          .then(() => dispatch(getIreneDashboard({ propertyId })))
      }
    >
      Add now
    </Link>
  </Notification>
);

const FastPaymentsBanner = ({ onClose, className }: CustomNotificationProps): React.ReactNode => (
  <Notification type={notificationTypes.SUCCESS} onClose={onClose}>
    <strong>Rent collection just got faster.</strong> Payments are now deposited into your bank account within two
    business days.{' '}
    <Link
      className="PaymentsNotification-Link"
      href="https://www.zillow.help/article/What-are-Fast-Payments"
      target="_blank"
      rel="noopener noreferrer"
    >
      Learn more
    </Link>
  </Notification>
);

export {
  CoverageRequestError,
  InBetweenTerms,
  HostedOnboardingRequired,
  ContinuePaymentSetup,
  InviteNewRenter,
  MoveInCostPaymentLimitError,
  PaymentTermEligibleForRentalProtectionPolicy,
  RentalProtectionFirstRentPaymentFailed,
  RentalProtectionLatestRentPaymentFailed,
  RentalProtectionCoverageRequestNotSigned,
  RentalProtectionPolicyPending,
  RentalProtectionMissedRentPayment,
  FastPaymentsBanner,
};
